import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Revenue } from '../types/finance';
import { getAllRevenues, createRevenue, updateRevenue, type RevenueQuery, type RevenueCreateDto, type RevenuePatchDto } from '../lib/api/endpoints';
import { revenueFromDto } from '../lib/api/mappers';

export type RevenueFilter = Omit<RevenueQuery, 'page' | 'pageSize'>;

/**
 * FE-32 — el listado de ingresos y las dos operaciones que lo mueven. Mismo
 * contrato que `useExpenses`: la visibilidad la aplica el servidor, los
 * errores viajan como `ApiError`, y `registrar`/`corregir` devuelven el ingreso
 * hidratado (cliente, tarifa, placa, soporte vigente) y lo reemplazan en sitio.
 */
export function useRevenues(filter: RevenueFilter = {}) {
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const filterKey = JSON.stringify(filter);

  useEffect(() => {
    let cancelled = false;
    const parsed = JSON.parse(filterKey) as RevenueFilter;
    const load = async () => {
      setIsLoading(true);
      try {
        const items = await getAllRevenues(parsed);
        if (cancelled) return;
        setRevenues(items.map(revenueFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setRevenues([]);
          setError(err instanceof Error ? err : new Error('Error desconocido cargando los ingresos'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [filterKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  const upsert = useCallback((r: Revenue) => {
    setRevenues(prev => {
      const idx = prev.findIndex(x => x.id === r.id);
      if (idx !== -1) {
        const next = [...prev];
        next[idx] = r;
        return next;
      }
      return [...prev, r].sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
    });
  }, []);

  const acciones = useMemo(() => ({
    registrar: async (data: RevenueCreateDto): Promise<Revenue> => {
      const r = revenueFromDto(await createRevenue(data));
      upsert(r);
      return r;
    },
    corregir: async (revenueId: string, data: RevenuePatchDto): Promise<Revenue> => {
      const r = revenueFromDto(await updateRevenue(revenueId, data));
      upsert(r);
      return r;
    },
    reemplazar: upsert,
  }), [upsert]);

  return { revenues, isLoading, error, reload, ...acciones };
}
